import type { ExperimentSchedule, ScheduledVariantWeight } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { toast } from '@/hooks/use-toast';
import { apiErrorMessage } from '../api-error';

export type SchedulePayload = {
  startAt: number | null;
  timezone: string | null;
  variantWeights: ScheduledVariantWeight[] | null;
  stopAt: number | null;
  stopOnSignificance: boolean;
  minRuntimeMs: number | null;
};

/**
 * Its own hook rather than more surface on useExperiment: a schedule is loaded on one
 * tab, changes without the experiment changing, and most experiments never have one.
 */
export const useSchedule = (experimentId: number) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();

  const { data: schedule, isLoading } = useQuery<ExperimentSchedule | null>({
    enabled: !!experimentId,
    queryKey: ['EXPERIMENT_SCHEDULE', experimentId],
    queryFn: () => apiClient.get<ExperimentSchedule | null>(`/experiments/${experimentId}/schedule`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT_SCHEDULE', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  /**
   * The server's rejection message is surfaced verbatim. Every one of them names the
   * field and says why, and rewording them here would mean maintaining two
   * explanations of the same rule.
   */
  const saveSchedule = async (payload: SchedulePayload) => {
    try {
      await apiClient.put(`/experiments/${experimentId}/schedule`, payload);
      invalidate();
      return true;
    } catch (error) {
      toast({
        title: 'Could not save the schedule',
        description: apiErrorMessage(error),
      });
      return false;
    }
  };

  const removeSchedule = async () => {
    await apiClient.delete(`/experiments/${experimentId}/schedule`);
    invalidate();
  };

  const isScheduledToStart = !!schedule && schedule.state === 'armed' && schedule.startAt !== null;
  const isHeld = schedule?.state === 'held';
  const isBlocked = schedule?.state === 'blocked';

  return {
    schedule: schedule ?? null,
    isLoading,
    saveSchedule,
    removeSchedule,
    isScheduledToStart,
    isHeld,
    isBlocked,
  };
};
